import { useEffect, useMemo, useState } from "react";
import api from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Search, UserPlus, Users } from "lucide-react";
import { LearnerTable, sortRows } from "@/components/t4r/learners/LearnerTable";
import { BulkBar } from "@/components/t4r/learners/BulkBar";
import { InviteLearnerDialog } from "@/components/t4r/learners/InviteLearnerDialog";
import { LearnerProgressDialog } from "@/components/t4r/LearnerProgressDialog";
import { exportLearnersCsv } from "@/components/t4r/learners/exportCsv";
import { useSelection, useSort, matchesSearch } from "@/components/t4r/learners/useLearnerList";
import { AddLearnersDialog } from "@/components/t4r/course/AddLearnersDialog";

export const CourseLearnersTab = ({ course, reload }) => {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [courses, setCourses] = useState([]);
  const { selected, toggle, setMany, clear } = useSelection();
  const { sort, onSort } = useSort();
  useEffect(() => { api.get("/lms/admin/courses").then(({ data }) => setCourses(data.courses || [])).catch(() => {}); }, []);

  const rows = useMemo(() => sortRows(course.learners.filter((l) => matchesSearch(l, q)), sort, "course_pct"), [course.learners, q, sort]);
  const slug = course.title.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase();

  return (
    <div className="space-y-3 pb-16" data-testid="course-learners-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search learners on this course" className="pl-8" data-testid="course-learner-search" />
        </div>
        <Button size="sm" variant="outline" className="gap-1" disabled={!rows.length} onClick={() => exportLearnersCsv(rows, `${slug}-learners.csv`)} data-testid="course-export-csv"><Download size={12} /> Export</Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => setAdding(true)} data-testid="course-add-learners"><Users size={12} /> Add existing</Button>
        <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" onClick={() => setInviting(true)} data-testid="course-invite-learner"><UserPlus size={12} /> Invite new</Button>
      </div>
      <LearnerTable rows={rows} selected={selected} onToggle={toggle} onToggleAll={(on) => setMany(rows.map((r) => r.id), on)} sort={sort} onSort={onSort} onOpen={setViewing} pctKey="course_pct" showCourses={false} />
      <BulkBar ids={[...selected]} fixedCourseId={course.id} actions={["unenrol", "resend", "suspend", "reactivate", "consent_yes", "consent_no", "opt_in", "opt_out"]} onDone={() => { clear(); reload(); }} onClear={clear} />
      <AddLearnersDialog open={adding} onClose={() => setAdding(false)} course={course} onAdded={reload} />
      <InviteLearnerDialog open={inviting} onClose={() => setInviting(false)} courses={courses} defaultCourseId={course.id} onInvited={reload} />
      <LearnerProgressDialog learnerId={viewing} onClose={() => setViewing(null)} />
    </div>
  );
};
